import React, { useCallback, useEffect, useState } from 'react'
import { Box, MuiThemeProvider, Theme, Typography } from '@material-ui/core'
import { materialAtlasTheme } from 'material-atlas/theme'
import authenticationManager from 'businesslayer/authenticationManager'
import GlobalProgressModal from 'components/global-progress-modal/global-progress-modal'
import { useMinutesManager } from './use-minutes-manager'
import {
    PageLayout,
    PageHeader,
    PageBody,
    MinutesManagerToolbar,
    PageFooter
} from 'components/layout/page-layout'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import i18n from 'i18next'

import { addKeyPressHandler, updatePendoMetadata } from 'common/util/functions'
import Minutesdocuments from 'components/minutes-documents/minutes-documents'
import { AtlasSelect } from 'components/atlas-material/select'
import { makeStyles } from '@material-ui/core'
import { statusMenuItems } from 'components/popovers/status-list/status-menu-items'
import { css } from '@emotion/react'
import { useMinutesList } from 'businesslayer/networkrequests/minutes/useMinutesList'
import { CommitteeSelect } from 'components/popovers/committee-select/committee-select'
import { useCommitteesState } from 'components/contexts/committee-context'
import { AtlasButton } from 'components/atlas-material/button'
import PlusIcon from 'assets/icons/PlusIcon'
import { ColorBaseMulberryBlue, ColorBaseWhite, ColorEastBay } from 'assets/styles/variables'
import LinearIndeterminate from 'components/shared/LinearProgress'
import {
    setSessionStorageItem,
    getSessionStorageItem,
    getLocalStorageItem
} from 'businesslayer/minutesLocalStore'
import { useHistory } from 'react-router-dom'
import { transientStorageManager } from 'businesslayer/minutesSessionStore'
import WritingStyleBanner from 'components/writing-style-banner/writing-style-banner'
import StatusToast from 'components/atlas-components/atlas-toast/Toast'
import { useMinutesPolling } from 'common/customHooks/pollingApiStatus'
import { POLLING_INTERVAL } from 'common/constants/constants'
import { shouldShowWritingStyleBanner } from 'components/minutetaker/components/Utils/Utils'

/**
 * Minutes Manager Page
 */

const useStyles = makeStyles((theme: Theme) => ({
    filterBar: {
        display: 'grid',
        gridTemplateColumns: ({ numColumns }: any) =>
            numColumns === 3 ? '2.5fr 1fr 1fr' : numColumns === 2 ? '3fr 1fr' : '3fr',
        gap: '24px',
        padding: '12px 24px'
    },
    minutesDocumentsText: {
        fontFamily: 'Source Sans Pro',
        color: '#1e1e1e',
        fontWeight: 600,
        fontSize: '18px',
        margin: 'auto 0px auto 0px'
    },
    noMinutesText: {
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '22px',
        padding: '24px',
        minHeight: '68px',
        maxHeight: '98px',
        justifyContent: 'center',
        alignItems: 'center',
        height: '68px'
    },
    header: {
        justifyContent: 'space-between',
        alignItems: 'center',
        maxHeight: '100px',
        borderBottom: '1px solid #E6E6E6',
        padding: '24px',
        backgroundColor: '#FFFFFF',
        display: 'flex'
    },
    subHeaderText: {
        color: '#282E37',
        fontWeight: 600,
        fontSize: '28px',
        lineHeight: '32px'
    },
    subTitleText: {
        color: '#282E37',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '20px'
    },
    createMinutesContainerCls: {
        textTransform: 'none',
        backgroundColor: ColorEastBay,
        display: 'flex',
        color: '#fff',
        borderWidth: '0px',
        borderRadius: '3px',
        letterSpacing: '0.5px',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        padding: '8px 12px',
        fontWeight: 600,
        placeContent: 'center',
        gap: '8px',
        minWidth: '168px',
        '&:focus, &:active': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px ${ColorBaseWhite}, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px',
            backgroundColor: `${ColorEastBay}`
        },
        '&:hover': {
            backgroundColor: '#364262',
            color: '#fff',
            cursor: 'pointer'
        }
    },
    createMinutesTxtCls: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        placeContent: 'center',
        gap: '5px'
    },
    center: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 300px)'
    },
    loaderStyle: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        height: '104px'
    },
    loaderText: {
        paddingLeft: '1%',
        color: theme.palette.text.disabled,
        textAlign: 'center',
        fontFamily: 'Source Sans Pro',
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 600,
        marginTop: theme.spacing(2)
    },
    fullScreenLoader: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.8)', // Semi-transparent background
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999 // Ensure the loader is on top of other elements
    },
    loaderContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    pageBodyWithBanner: {
        '& > div > div': {
            maxHeight: 'calc(100vh - 420px) !important',
            '@media (min-width: 768px) and (max-width: 1180px)': {
                maxHeight: 'calc(100vh - 470px) !important'
            }
        }
    }
}))

declare global {
    interface Window {
        canAddMinutes: boolean
    }
}

export const MinutesManagerPage = () => {
    const { fetchMinutesDocuments, documentLists, isLoading, totalDocuments } = useMinutesList()
    const {
        statusFilter,
        canAddMinutes,
        handleOpenMinutesModal,
        handleOpenAboutModal,
        handleOpenMinutes,
        handleClickMinutesOptions,
        setStatusFilter,
        modals,
        toastNotifications,
        isAdminOfCommittee,
        deleteResponse,
        setDeleteResponse,
        updatedMinutesItem,
        setUpdatedMinutesItem,
        isDeleting
    } = useMinutesManager()

    const [page, setPage] = useState<number>(1)
    const [committeeValue, setCommitteeValue] = useState<Committee>()
    const [sortBy, setSortBy] = useState<string>('updated_at')
    const [order, setOrder] = useState<string>('desc')
    const { committees } = useCommitteesState()
    const {
        minutesDocuments,
        transcriptDocuments,
        isPolling,
        fetchMinutesDocuments: fetchPollingMinutesDocuments,
        toasts,
        setToasts
    } = useMinutesPolling()

    const history = useHistory()

    // Update product analytics metadata after roles load (Pendo + Amplitude)
    if (window.pendo?.isInitialized || window.amplitudeAnalyticsEnabled) {
        const role = canAddMinutes ? 'Administrator' : 'Reviewer'
        updatePendoMetadata({ role }, true)
    }

    const handleStatusFilterChange = (event: any) => {
        setStatusFilter(event.target.value)
        setSessionStorageItem('selectedStatusFilter', event.target.value)
        setPage(1)
        fetchMinutesDocuments(event.target.value, committeeValue?.id, 1, sortBy, order)
    }
    const handlePageChange = (page: number) => {
        setPage((prev) => prev + 1)
        fetchMinutesDocuments(statusFilter, committeeValue?.id, page, sortBy, order)
    }
    const handleCommiitteeChange = (value: Committee) => {
        setCommitteeValue(value)
        setSessionStorageItem('selectedCommitteeFilter', value.id)
        setPage(1)
        fetchMinutesDocuments(statusFilter, value.id, 1, sortBy, order)
    }
    const handleSortBy = (sortBy: string, order: string) => {
        setSortBy(sortBy)
        setPage(1)
        setOrder(order)
        fetchMinutesDocuments(statusFilter, committeeValue?.id, 1, sortBy, order)
    }
    const obj = {
        id: ' ',
        is_admin: true,
        name: i18n.t('ALL_COMMITTEE')
    }

    useEffect(() => {
        const selectedStatusFilter = getSessionStorageItem(
            'selectedStatusFilter'
        ) as DocumentStatusFilter
        const selectedCommitteeFilter = getSessionStorageItem('selectedCommitteeFilter')

        if (selectedCommitteeFilter) {
            const selectedCommittee = committees.find(
                (committee) => committee.id === selectedCommitteeFilter
            )
            setCommitteeValue(selectedCommittee)
        }
        if (selectedStatusFilter) {
            setStatusFilter(selectedStatusFilter)
        }

        if (deleteResponse || updatedMinutesItem) {
            setTimeout(() => {
                fetchMinutesDocuments(statusFilter, committeeValue?.id, page, sortBy, order)
                setDeleteResponse('')
                setUpdatedMinutesItem('')
            }, 0)
        }
    }, [
        deleteResponse,
        committeeValue,
        fetchMinutesDocuments,
        statusFilter,
        page,
        sortBy,
        order,
        setDeleteResponse,
        updatedMinutesItem,
        setUpdatedMinutesItem,
        setStatusFilter,
        committees
    ])

    // Consolidated redirect logic to prevent alternating behavior
    useEffect(() => {
        const handleRedirect = () => {
            const currentPath = window.location.pathname
            const hasRedirected = sessionStorage.getItem('hasRedirectedToActionDashboard')
            const redirectTimestamp = sessionStorage.getItem('redirectTimestamp')
            const currentTime = Date.now()
            const hasIntentionallyNavigatedHome = sessionStorage.getItem(
                'intentionallyNavigatedHome'
            )
            const landingPath = getLocalStorageItem('landingPath')
            const parentPlatform = getSessionStorageItem('parentPlatform')
            const isBoardsIOS = parentPlatform === 'boardsios'

            // Only apply this redirect logic for boardsios platform
            if (!isBoardsIOS) {
                return
            }

            // If we're already on actionDashboard, clear all flags and return
            if (currentPath === '/actionDashboard') {
                sessionStorage.removeItem('hasRedirectedToActionDashboard')
                sessionStorage.removeItem('redirectTimestamp')
                sessionStorage.removeItem('intentionallyNavigatedHome')
                return
            }

            // If user intentionally navigated to home, clear flags and don't redirect
            if (hasIntentionallyNavigatedHome === 'true' && currentPath === '/') {
                localStorage.removeItem('landingPath')
                sessionStorage.removeItem('hasRedirectedToActionDashboard')
                sessionStorage.removeItem('redirectTimestamp')
                return
            }

            // Check if we've already redirected recently (within 5 seconds)
            if (hasRedirected === 'true' && redirectTimestamp) {
                const timeSinceRedirect = currentTime - parseInt(redirectTimestamp)
                if (timeSinceRedirect <= 5000) {
                    return // Don't redirect again so soon
                }
                // Clear stale redirect flag if it's been too long
                if (timeSinceRedirect > 5000) {
                    sessionStorage.removeItem('hasRedirectedToActionDashboard')
                    sessionStorage.removeItem('redirectTimestamp')
                }
            }

            // Only redirect if we're on the home page and have a reason to redirect
            if (currentPath === '/') {
                let shouldRedirect = false

                // Check if we have a landingPath to actionDashboard
                if (landingPath === '/actionDashboard') {
                    const isDirectNavigation = window.history.length <= 2
                    const hasValidReferrer =
                        document.referrer && document.referrer.includes(window.location.hostname)
                    const isFromActionDashboard = document.referrer.includes('/actionDashboard')
                    const isFromParentApp = document.referrer === '' || !hasValidReferrer

                    shouldRedirect =
                        isFromParentApp ||
                        isFromActionDashboard ||
                        (isDirectNavigation && !hasValidReferrer)
                }

                // Check if we have a shouldRedirectToActionDashboard flag
                const shouldBeOnActionDashboard = localStorage.getItem(
                    'shouldRedirectToActionDashboard'
                )
                if (shouldBeOnActionDashboard === 'true') {
                    shouldRedirect = true
                }

                // Check if we're coming from actionDashboard
                if (document.referrer.includes('/actionDashboard') && window.history.length <= 2) {
                    shouldRedirect = true
                }

                // Check if we're on boardsios platform with no referrer (direct launch)
                if (
                    (!document.referrer || document.referrer === '') &&
                    parentPlatform === 'boardsios' &&
                    window.history.length <= 2 &&
                    landingPath === '/actionDashboard'
                ) {
                    shouldRedirect = true
                }

                // Perform the redirect if needed
                if (shouldRedirect) {
                    localStorage.removeItem('landingPath')
                    localStorage.removeItem('shouldRedirectToActionDashboard')
                    sessionStorage.setItem('hasRedirectedToActionDashboard', 'true')
                    sessionStorage.setItem('redirectTimestamp', currentTime.toString())
                    history.push('/actionDashboard')
                }
            }
        }

        // Add a small delay to ensure component is fully mounted
        const timeoutId = setTimeout(handleRedirect, 100)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [history])

    // Handle browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const currentPath = window.location.pathname
            const parentPlatform = getSessionStorageItem('parentPlatform')
            const isBoardsIOS = parentPlatform === 'boardsios'

            // Only apply this logic for boardsios platform
            if (!isBoardsIOS) {
                return
            }

            // If user navigates back to home using browser back button, mark as intentional
            if (currentPath === '/') {
                sessionStorage.setItem('intentionallyNavigatedHome', 'true')
                sessionStorage.removeItem('hasRedirectedToActionDashboard')
                sessionStorage.removeItem('redirectTimestamp')
            }

            // If user navigates to actionDashboard, clear all flags
            if (currentPath === '/actionDashboard') {
                sessionStorage.removeItem('hasRedirectedToActionDashboard')
                sessionStorage.removeItem('redirectTimestamp')
                sessionStorage.removeItem('intentionallyNavigatedHome')
            }
        }

        window.addEventListener('popstate', handlePopState)

        return () => {
            window.removeEventListener('popstate', handlePopState)
        }
    }, [])

    useEffect(() => {
        let intervalId: number | undefined
        if (isPolling) {
            fetchPollingMinutesDocuments()
            intervalId = setInterval(fetchPollingMinutesDocuments, POLLING_INTERVAL) as number
        }
        return () => {
            if (intervalId) {
                clearInterval(intervalId)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPolling])

    const handleCreateMinutes = useCallback(() => {
        handleOpenMinutesModal('CREATE')
    }, [handleOpenMinutesModal])

    const removeToast = useCallback(
        (toastId: number | string) => {
            setToasts((prev) => prev.filter((t) => t.id !== toastId))
        },
        [setToasts]
    )

    const classes = useStyles({
        numColumns: canAddMinutes ? 3 : 2
    })

    const showWritingStyleBanner = shouldShowWritingStyleBanner(canAddMinutes)

    return (
        <MuiThemeProvider theme={materialAtlasTheme}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
                <PageLayout>
                    <PageHeader
                        title={i18n.t('PAGE_TITLE_MM')}
                        onSignout={authenticationManager.handleSignout}>
                        <MinutesManagerToolbar />
                    </PageHeader>

                    <Box className={classes.header} data-testid="MinutesManager_hrader">
                        <Box>
                            <Typography
                                variant="h1"
                                component="h1"
                                className={classes.subHeaderText}>
                                {i18n.t('MINUTES_MANAGER')}
                            </Typography>
                            {canAddMinutes && (
                                <Typography
                                    variant="h6"
                                    component="h6"
                                    className={classes.subTitleText}>
                                    {i18n.t('MANAGE_MINUTES_HEADER_TITLE')}
                                </Typography>
                            )}
                        </Box>
                        {canAddMinutes && (
                            <AtlasButton
                                role="button"
                                tabIndex={0}
                                data-analytics="MM-Create-Minutes"
                                data-testid="MinutesManager_Create_Minutes_Btn"
                                disableRipple
                                className={classes.createMinutesContainerCls}
                                onKeyPress={(e) =>
                                    addKeyPressHandler(e) ? handleCreateMinutes : null
                                }
                                onClick={handleCreateMinutes}>
                                <Box
                                    className={classes.createMinutesTxtCls}
                                    data-testid="MinutesManager_Create_Minutes_ICON">
                                    <PlusIcon /> {i18n.t('CREATE_MINUTES')}
                                </Box>
                            </AtlasButton>
                        )}
                    </Box>
                    {<LinearIndeterminate loading={!isLoading || isDeleting} />}
                    <Box className={classes.filterBar}>
                        <Box
                            aria-label={i18n.t('MANAGE_YOUR_MINUTES')}
                            className={classes.minutesDocumentsText}>
                            {i18n.t('MANAGE_YOUR_MINUTES')}
                        </Box>
                        <CommitteeSelect
                            css={css`
                                flex: 1;
                                > label {
                                    color: #1e1e1e;
                                    font-family: 'Source Sans Pro';
                                    color: #1e1e1e;
                                    font-weight: 400;
                                    font-size: 13px;
                                    line-height: 16px;
                                }
                            `}
                            data-analytics="MM-Filter-FilterByCommittee"
                            label={i18n.t('FILTER_BY_COMITTEE')}
                            isReadonly={false}
                            onChange={handleCommiitteeChange}
                            value={committeeValue || obj}
                            committees={[...[obj], ...committees]}></CommitteeSelect>
                        {canAddMinutes && (
                            <AtlasSelect
                                css={css`
                                    flex: 1;
                                    > label {
                                        color: #1e1e1e;
                                        font-family: 'Source Sans Pro';
                                        color: #1e1e1e;
                                        font-weight: 400;
                                        font-size: 13px;
                                        line-height: 16px;
                                    }
                                `}
                                data-testid="MinutesManager_Filter"
                                role="combobox"
                                id="filter"
                                data-analytics="MM-Filter-FilterByStatus"
                                label={i18n.t('FILTER_BY_STATUS')}
                                ariaLabelledby="filter"
                                value={statusFilter}
                                onChange={handleStatusFilterChange}>
                                {statusMenuItems()}
                            </AtlasSelect>
                        )}
                    </Box>
                    {showWritingStyleBanner && <WritingStyleBanner />}

                    <Box className={showWritingStyleBanner ? classes.pageBodyWithBanner : ''}>
                        <PageBody>
                            {/* Always show the LoaderView when isLoading is true
                            Show existing records only when not loading
                        */}
                            <div>
                                {documentLists.length > 0 ? (
                                    <Minutesdocuments
                                        statusFilter={statusFilter}
                                        onClickOptions={handleClickMinutesOptions}
                                        onOpenMinutes={handleOpenMinutes}
                                        documentLists={documentLists}
                                        handlePageChange={handlePageChange}
                                        handleSortBy={handleSortBy}
                                        page={page}
                                        isAdminOfCommittee={isAdminOfCommittee}
                                        totalDocuments={totalDocuments}
                                        sortBy={sortBy}
                                        order={order}
                                        minutesDocuments={minutesDocuments}
                                        transcriptDocuments={transcriptDocuments}
                                    />
                                ) : isLoading ? (
                                    <Box className={classes.noMinutesText}>
                                        {typeof committeeValue?.name === undefined &&
                                        statusFilter === 'all' ? (
                                            <div>{i18n.t('NO_MINUTES_TEXTS')}</div>
                                        ) : (
                                            <div>
                                                <h5>{i18n.t('NO_MINUTES_FOUND')}</h5>
                                                {i18n.t('NO_MINUTES_FOUND_ALT')}
                                            </div>
                                        )}
                                    </Box>
                                ) : null}
                            </div>
                        </PageBody>
                    </Box>
                    <PageFooter
                        platform={transientStorageManager?.platform || undefined}
                        onAboutClick={handleOpenAboutModal}
                    />
                    <div className="custom-toast-container">
                        {Array.from(new Set(toasts.map((toast) => toast.id))).map((id) => {
                            const toast = toasts.find((t) => t.id === id)
                            return toast?.view_toast === true ? (
                                <StatusToast key={toast.id} {...toast} removeToast={removeToast} />
                            ) : null
                        })}
                    </div>
                </PageLayout>
                {modals}
                {toastNotifications}
                <GlobalProgressModal />
            </MuiPickersUtilsProvider>
        </MuiThemeProvider>
    )
}
